import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MessageSquare, Workflow, Zap, Settings, Code } from 'lucide-react';

export const Demo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chat' | 'flow'>('chat');
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section id="demo" className="relative py-24 overflow-hidden">
      <div className="relative z-10 container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Powerful Features <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Ready to Use</span>
          </h2>
          <p className="text-xl text-gray-400">
            Create your first AI assistant in minutes with our intuitive platform
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'chat'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <MessageSquare size={20} />
            Conversation Flow
          </button>
          <button
            onClick={() => setActiveTab('flow')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'flow'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Workflow size={20} />
            Visual Builder
          </button>
        </div>

        {/* Demo Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-2xl p-8"
          >
            {activeTab === 'chat' ? (
              <div className="space-y-6">
                {/* Chat Flow Example */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <MessageSquare className="text-purple-400" size={24} />
                      Natural Conversations
                    </h3>
                    <ul className="space-y-3 text-gray-300">
                      <li className="flex items-start gap-3">
                        <Zap className="text-green-400 flex-shrink-0 mt-1" size={16} />
                        <span>Intelligent context understanding across multiple messages</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Zap className="text-green-400 flex-shrink-0 mt-1" size={16} />
                        <span>Automatic intent recognition and routing</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Zap className="text-green-400 flex-shrink-0 mt-1" size={16} />
                        <span>Multi-turn conversation memory</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Zap className="text-green-400 flex-shrink-0 mt-1" size={16} />
                        <span>Real-time response streaming</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                    <div className="space-y-4">
                      <div className="flex justify-end">
                        <div className="bg-purple-500 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-xs">
                          I need help with my order
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-gray-700 text-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-xs">
                          I'd be happy to help! What's your order number?
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="bg-purple-500 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-xs">
                          #12345
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-gray-700 text-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-xs">
                          Found it! Your order is in transit and will arrive Friday.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Flow Builder Example */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <Workflow className="text-purple-400" size={24} />
                      Visual Flow Builder
                    </h3>
                    <ul className="space-y-3 text-gray-300">
                      <li className="flex items-start gap-3">
                        <Settings className="text-blue-400 flex-shrink-0 mt-1" size={16} />
                        <span>Drag-and-drop conversation design</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Settings className="text-blue-400 flex-shrink-0 mt-1" size={16} />
                        <span>Conditional logic and branching paths</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Settings className="text-blue-400 flex-shrink-0 mt-1" size={16} />
                        <span>API integrations and webhooks</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Settings className="text-blue-400 flex-shrink-0 mt-1" size={16} />
                        <span>Real-time testing and debugging</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                    <div className="space-y-4">
                      {/* Flow visualization */}
                      <div className="flex items-center justify-between">
                        <div className="bg-purple-500/20 border border-purple-500 rounded-lg px-4 py-2 text-purple-300 text-sm">
                          Start
                        </div>
                        <div className="h-0.5 w-8 bg-gray-600"></div>
                        <div className="bg-blue-500/20 border border-blue-500 rounded-lg px-4 py-2 text-blue-300 text-sm">
                          Greeting
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="h-8 w-0.5 bg-gray-600"></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="bg-green-500/20 border border-green-500 rounded-lg px-4 py-2 text-green-300 text-sm">
                          Get Order
                        </div>
                        <div className="h-0.5 w-8 bg-gray-600"></div>
                        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg px-4 py-2 text-yellow-300 text-sm">
                          Check Status
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="h-8 w-0.5 bg-gray-600"></div>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="bg-pink-500/20 border border-pink-500 rounded-lg px-4 py-2 text-pink-300 text-sm">
                          Send Response
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 text-center">
              <motion.a
                href="/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
              >
                <Code size={20} />
                Start Building Now
              </motion.a>
              <p className="text-gray-400 text-sm mt-3">
                Full visual editor available in the dashboard
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
